/* eslint-disable no-nested-ternary */
import { useState } from 'react';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import {
  alpha,
  Button,
  Divider,
  Link,
  ListItemText,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tabs,
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useBoolean } from 'src/hooks/use-boolean';

import { hasPermission } from 'src/utils/has-permissions';
import { fCurrencyRupees } from 'src/utils/format-number';
import { fAPIDate, fDateTime } from 'src/utils/format-time';
import { TABLE_HEAD_CELL_TYPE } from 'src/utils/common-types';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { TableHeadCustom } from 'src/components/table';

import { ChequeCustomerType, ChequeStatus, IChequeItem } from 'src/types/cheque';

import UpdateChequeStatus from './update-cheque-status';

const LINE_ITEM_HEADERS: TABLE_HEAD_CELL_TYPE[] = [
  { id: 'date', label: 'Date', align: 'left', primary: true },
  {
    id: 'holder',
    label: 'Holder',
    align: 'left',
    primary: true,
  },
  { id: 'status', label: 'Status', align: 'center', primary: true },
  { id: 'updatedBy', label: 'Updated By', align: 'right', primary: true },
  { id: 'updatedAt', label: 'Updated At', align: 'right', primary: true },
];

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'history', label: 'History' },
];

type Props = {
  currentCheque?: IChequeItem;
};

export default function ChequeDetails({ currentCheque }: Props) {
  const [tabValue, setTabValue] = useState('overview');

  const { user: authUser } = useAuthContext();

  const confirm = useBoolean();

  return (
    <>
      <Grid container spacing={3}>
        <Grid xs={12}>
          <Card sx={{ p: 3 }}>
            <Tabs
              value={tabValue}
              onChange={(_, value) => setTabValue(value)}
              sx={{
                px: 2.5,
                boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
              }}
            >
              {TABS.map((tab) => (
                <Tab key={tab.value} iconPosition="end" value={tab.value} label={tab.label} />
              ))}
            </Tabs>

            {tabValue === 'overview' && (
              <Grid container spacing={2} mt={2}>
                <Grid
                  mb={1}
                  display="flex"
                  mt={2}
                  alignItems="center"
                  justifyContent="space-between"
                  width={1}
                >
                  <Typography variant="subtitle1">Basic Information</Typography>

                  {hasPermission(
                    authUser,
                    'any',
                    AuthPermissions.UPDATE_CHEQUE,
                    AuthPermissions.ADMIN_PERMISSION,
                    AuthPermissions.SUPER_ADMIN_PERMISSION
                  ) && (
                    <Link
                      component={RouterLink}
                      href={paths.dashboard.cheque.edit(currentCheque?._id ?? '')}
                    >
                      <Button variant="text" startIcon={<Iconify icon="solar:pen-bold" />}>
                        Edit
                      </Button>
                    </Link>
                  )}
                </Grid>

                <Grid xs={12} md={3}>
                  <ListItemText
                    primary="Cheque Number"
                    secondary={currentCheque?.chequeNumber}
                    primaryTypographyProps={{ typography: 'body2' }}
                    secondaryTypographyProps={{
                      component: 'span',
                      color: 'text.disabled',
                    }}
                  />
                </Grid>

                <Grid xs={12} md={3}>
                  <ListItemText
                    primary="Amount"
                    secondary={fCurrencyRupees(currentCheque?.amountInCents ?? 0)}
                    primaryTypographyProps={{ typography: 'body2' }}
                    secondaryTypographyProps={{
                      component: 'span',
                      color: 'text.disabled',
                    }}
                  />
                </Grid>

                <Grid xs={12} md={3}>
                  <ListItemText
                    primary="Bank"
                    secondary={currentCheque?.bank}
                    primaryTypographyProps={{ typography: 'body2' }}
                    secondaryTypographyProps={{
                      component: 'span',
                      color: 'text.disabled',
                    }}
                  />
                </Grid>

                <Grid xs={12} md={3}>
                  <ListItemText
                    primary="Cheque Date"
                    secondary={fAPIDate(currentCheque?.chequeDate)}
                    primaryTypographyProps={{ typography: 'body2' }}
                    secondaryTypographyProps={{
                      component: 'span',
                      color: 'text.disabled',
                    }}
                  />
                </Grid>

                <Grid xs={12} md={3}>
                  <ListItemText
                    primary="Received Date"
                    secondary={fAPIDate(currentCheque?.receivedDate)}
                    primaryTypographyProps={{ typography: 'body2' }}
                    secondaryTypographyProps={{
                      component: 'span',
                      color: 'text.disabled',
                    }}
                  />
                </Grid>

                <Divider
                  sx={{
                    my: 2.5,
                    typography: 'overline',
                    color: 'text.disabled',
                    '&:before, :after': {
                      borderTopStyle: 'dashed',
                    },
                  }}
                />

                <Grid xs={12}>
                  <Typography variant="subtitle1" sx={{ my: 1 }}>
                    Operational Information
                  </Typography>
                </Grid>

                <Grid xs={12} md={3}>
                  <ListItemText
                    primary="Customer"
                    secondary={
                      currentCheque?.invoice ? (
                        <a href={currentCheque.invoice} target="_blank" rel="noopener noreferrer">
                          {currentCheque.customer}
                        </a>
                      ) : (
                        currentCheque?.customer
                      )
                    }
                    primaryTypographyProps={{ typography: 'body2' }}
                    secondaryTypographyProps={{
                      component: 'span',
                      color: 'text.disabled',
                    }}
                  />
                </Grid>

                <Grid xs={12} md={3}>
                  <ListItemText
                    primary="Sale Type"
                    secondary={
                      <Label
                        variant="soft"
                        color={
                          (currentCheque?.type === ChequeCustomerType.SALE && 'default') ||
                          (currentCheque?.type === ChequeCustomerType.WHOLESALE && 'info') ||
                          'default'
                        }
                      >
                        {currentCheque?.type?.split('_').join(' ')}
                      </Label>
                    }
                    primaryTypographyProps={{ typography: 'body2' }}
                    secondaryTypographyProps={{
                      component: 'span',
                      color: 'text.disabled',
                      textTransform: 'capitalize',
                    }}
                  />
                </Grid>

                <Grid xs={12} md={3}>
                  <ListItemText
                    primary="Holder"
                    secondary={currentCheque?.holder}
                    primaryTypographyProps={{ typography: 'body2' }}
                    secondaryTypographyProps={{
                      component: 'span',
                      color: 'text.disabled',
                    }}
                  />
                </Grid>

                <Grid xs={12} md={3}>
                  <ListItemText
                    primary="Status"
                    secondary={
                      <Label
                        variant="soft"
                        color={
                          (currentCheque?.status === ChequeStatus.IN_HAND && 'default') ||
                          (currentCheque?.status === ChequeStatus.PAID && 'success') ||
                          (currentCheque?.status === ChequeStatus.RETURN && 'error') ||
                          'default'
                        }
                      >
                        {currentCheque?.status.split('-').join(' ')}
                      </Label>
                    }
                    primaryTypographyProps={{ typography: 'body2' }}
                    secondaryTypographyProps={{
                      component: 'span',
                      color: 'text.disabled',
                    }}
                  />
                </Grid>

                <Grid xs={12} md={3}>
                  <ListItemText
                    primary={
                      <Link
                        component={RouterLink}
                        href={paths.dashboard.user.view(currentCheque?.updatedBy._id ?? '')}
                      >
                        {`${currentCheque?.updatedBy.firstName} ${currentCheque?.updatedBy.lastName}`}
                      </Link>
                    }
                    secondary="Last Updated"
                    primaryTypographyProps={{ typography: 'body2' }}
                    secondaryTypographyProps={{
                      component: 'span',
                      color: 'text.disabled',
                    }}
                  />
                </Grid>
              </Grid>
            )}

            {tabValue === 'history' && (
              <Grid xs={12}>
                <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '300px' }}>
                  <Scrollbar>
                    <Table stickyHeader size="small" sx={{ minWidth: 960 }}>
                      <TableHeadCustom headLabel={LINE_ITEM_HEADERS} />

                      <TableBody>
                        {currentCheque?.history.map((history, index) => (
                          <TableRow hover key={history._id}>
                            <TableCell align="left">{fAPIDate(history.date)}</TableCell>

                            <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                              {history.holder}
                            </TableCell>

                            <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                              <Label
                                variant="soft"
                                color={
                                  (history.status === ChequeStatus.PAID && 'success') ||
                                  (history.status === ChequeStatus.RETURN && 'error') ||
                                  (history.status === ChequeStatus.SETTLED && 'warning') ||
                                  (history.status === ChequeStatus.RECOVERED && 'success') ||
                                  (history.status === ChequeStatus.HANDOVER && 'info') ||
                                  (history.status === ChequeStatus.IN_HAND && 'default') ||
                                  'default'
                                }
                              >
                                {history.status?.split('_').join(' ')}
                              </Label>
                            </TableCell>

                            <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                              {history.updatedBy.firstName}
                            </TableCell>

                            <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                              {fDateTime(history.createdAt)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Scrollbar>
                </TableContainer>
              </Grid>
            )}

            <Stack direction="row" justifyContent="flex-end" gap={2} mt={2}>
              <Link component={RouterLink} href={paths.dashboard.cheque.root()}>
                <Button variant="outlined" color="inherit">
                  Cancel
                </Button>
              </Link>

              {hasPermission(
                authUser,
                'any',
                AuthPermissions.UPDATE_CHEQUE_STATUS,
                AuthPermissions.ADMIN_PERMISSION,
                AuthPermissions.SUPER_ADMIN_PERMISSION
              ) &&
                currentCheque?.status !== ChequeStatus.RECOVERED && (
                  <Button variant="contained" type="button" onClick={() => confirm.onTrue()}>
                    Change Status
                  </Button>
                )}
            </Stack>
          </Card>
        </Grid>
      </Grid>
      {currentCheque && (
        <UpdateChequeStatus
          currentCheque={currentCheque}
          isOpen={confirm.value}
          onClose={() => confirm.onFalse()}
        />
      )}
    </>
  );
}
